/* The cookie bar: brand/docs/04-components.md, Cookie notice (#33). Markup in
 * snippets/halo-gdpr-cookie-consent.liquid.
 *
 * It asks through Shopify's Customer Privacy API and stands down whenever Shopify's own cookie
 * banner or its preferences are in the page, shown or hidden, so a visitor meets exactly one
 * notice: Shopify's until it is switched off in the admin, this bar after. (Shopify keeps its
 * banner in the page, hidden, once the visitor has answered it.)
 *
 * It shows while Shopify.customerPrivacy.shouldShowBanner() is true, and on a theme preview
 * until the visitor answers, as Shopify's own banner does there. „Accept" sets every category
 * yes, „Refuz" every one no, „Setări" opens the categories and „Salvează" stores them; the API
 * remembers the answer. Anything marked [data-cookie-reopen] (the footer's „Setări cookie")
 * reopens the categories with the stored answer ticked.
 */
(() => {
  if (customElements.get('cookie-consent')) return;

  // Shopify's banner and its preferences dialog.
  const SHOPIFY_UI = ['shopify-pc__banner', 'shopify-pc__prefs'];
  // When Shopify's banner script is on the page: how long after load it may take to arrive.
  const WAIT_FOR_SHOPIFY = 2000;
  const KEYS = ['marketing', 'analytics', 'preferences'];
  // One answer for every category, by name: { marketing: true, analytics: true, preferences: true }.
  const every = (yes) => Object.fromEntries(KEYS.map((key) => [key, yes]));

  class CookieConsent extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-cookie-panel]');
      this.settingsButton = this.querySelector('[data-cookie-settings]');
      this.boxes = [...this.querySelectorAll('input[type=checkbox][name]')];

      this.querySelector('[data-cookie-accept]').addEventListener('click', () => this.answer(every(true)));
      this.querySelector('[data-cookie-decline]').addEventListener('click', () => this.answer(every(false)));
      this.querySelector('[data-cookie-save]').addEventListener('click', () => {
        const on = (name) => this.boxes.some((box) => box.name === name && box.checked);
        this.answer(Object.fromEntries(KEYS.map((key) => [key, on(key)])));
      });
      this.settingsButton.addEventListener('click', () => this.setPanel(this.panel.hidden));
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || this.panel.hidden) return;
        this.setPanel(false);
        if (!this.hidden) this.settingsButton.focus();
      });

      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-cookie-reopen]');
        if (!trigger) return;
        event.preventDefault();
        this.reopen(trigger);
      });
      document.addEventListener('visitorConsentCollected', () => this.evaluate());

      this.watchShopify();
      if (!document.querySelector('script[src*="/privacy-banner/storefront-banner.js"]')) {
        this.settled = true;
      } else {
        const later = () => setTimeout(() => { this.settled = true; this.evaluate(); }, WAIT_FOR_SHOPIFY);
        if (document.readyState === 'complete') later();
        else window.addEventListener('load', later, { once: true });
      }

      if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (error || !window.Shopify.customerPrivacy) return;
        this.api = window.Shopify.customerPrivacy;
        this.evaluate();
      });
    }

    // Shopify adds its banner and preferences to the body: re-evaluate when they come or go.
    watchShopify() {
      new MutationObserver(() => this.evaluate()).observe(document.body, { childList: true });
    }

    shopifyInPage() {
      return SHOPIFY_UI.some((id) => document.getElementById(id));
    }

    stored() {
      const consent = (this.api.currentVisitorConsent && this.api.currentVisitorConsent()) || {};
      return KEYS.some((key) => consent[key] === 'yes' || consent[key] === 'no');
    }

    due() {
      if (this.answered || this.stored()) return false;
      return this.api.shouldShowBanner() === true || window.Shopify.previewMode === true;
    }

    evaluate() {
      if (!this.api) return;
      if (this.shopifyInPage()) {
        this.show(false);
        return;
      }
      if (this.reopened || !this.settled) return;
      this.show(this.due());
    }

    show(on) {
      if (this.hidden === !on) return;
      this.hidden = !on;
      if (!on) this.setPanel(false);
    }

    setPanel(open) {
      this.panel.hidden = !open;
      this.settingsButton.setAttribute('aria-expanded', String(open));
      if (open || !this.reopened) return;
      // Reopened from the footer and closed again without a new answer: the stored one stands.
      this.reopened = false;
      this.show(false);
      this.restoreFocus();
    }

    reopen(trigger) {
      if (!this.api) return;
      if (this.shopifyInPage()) {
        if (window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function') window.privacyBanner.showPreferences();
        return;
      }
      const consent = (this.api.currentVisitorConsent && this.api.currentVisitorConsent()) || {};
      for (const box of this.boxes) box.checked = consent[box.name] === 'yes';
      this.trigger = trigger;
      this.hidden = false;
      this.setPanel(true);
      this.reopened = true;
      (this.boxes[0] || this.settingsButton).focus();
    }

    // The visitor's answer, one boolean per category by name (KEYS).
    answer({ preferences, analytics, marketing }) {
      this.answered = true;
      this.reopened = false;
      this.show(false);
      this.restoreFocus();
      try {
        this.api.setTrackingConsent({ preferences, analytics, marketing, sale_of_data: marketing }, () => {});
      } catch (error) {
        // Nothing stored: the bar asks again on the next page.
      }
    }

    restoreFocus() {
      if (this.trigger && this.trigger.isConnected) this.trigger.focus();
      this.trigger = null;
    }
  }

  customElements.define('cookie-consent', CookieConsent);
})();
